// src/components/PrivateRoute.js
import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { getRouteMenuPermission, userHasCompanyRole } from '../config/subscriptionConfig';

const PrivateRoute = ({ children, roles, allowCoordinator = false, allowInspector = false, strictRoles = false, allowLeaveApprover = false }) => {
  const { user, loading } = useContext(UserContext);
  const location = useLocation();

  if (loading) {
    return <div>Ładowanie...</div>;
  }

  if (!user || !user.accessToken) {
    console.warn("❌ Użytkownik niezalogowany - przekierowanie do /login");
    return <Navigate to="/login" replace />;
  }

  // Akceptujący urlopy (grupa akceptacji w Time & Attendance — także nie-admin): nadanie
  // przez admina w grupie JEST uprawnieniem do tej trasy, niezależnie od roli/menu_permissions.
  // Backend i tak pokazuje mu wyłącznie wnioski członków jego grup.
  if (allowLeaveApprover && user.approvesLeave === true) {
    return children;
  }

  // strictRoles: wpuszcza WYŁĄCZNIE rola z listy — bez furtek coordinator/inspector
  // i bez roli firmowej. Trasy /ecc/* nie są zmapowane w ROUTE_MENU_PERMISSION,
  // więc fallback niżej wpuszczał na nie każdego z niepustym menuPermissions.
  if (strictRoles && !(roles && roles.includes(user.role))) {
    console.warn("⛔ [PrivateRoute] Trasa tylko dla ról:", roles, "- przekierowanie do /");
    return <Navigate to="/" replace />;
  }

  // Sprawdź czy użytkownik ma odpowiednią rolę LUB jest coordinatorem/inspektorem
  const hasRoleAccess = roles && roles.includes(user.role);
  const hasCoordinatorAccess = allowCoordinator && user.isCoordinator;
  const hasInspectorAccess = allowInspector && user.inspector;
  const hasAnyAccess = hasRoleAccess || hasCoordinatorAccess || hasInspectorAccess;
  // Rola firmowa zawęża (także rola z pustą listą — ta nie otwiera nic); brak roli nie.
  const hasMenuPermissions = userHasCompanyRole(user);
  const menu = user.menuPermissions || [];

  // Trasy osobiste (roles zawiera user/userves) nie podlegają menu_permissions —
  // odpowiednik "user items" (przed separator-admin) w sidebarze.
  const isUserRoute = roles && (roles.includes('user') || roles.includes('userves'));
  // Klucz uprawnienia kategorii dla tej trasy (zgodny z hasAccessToMenuItem w sidebarze).
  const permissionKey = isUserRoute ? null : getRouteMenuPermission(location.pathname);
  // Dostęp przez rolę firmową: gdy mamy klucz trasy — wymagamy go na whitelist;
  // gdy trasa nie jest zmapowana — zgrubny fallback (jak dotychczas).
  const hasCompanyRoleAccess = menu.length > 0 && (
    permissionKey ? menu.includes(permissionKey) : true
  );

  if (roles) {
    // Te same zasady co admin: użytkownik z przypisaną rolą firmową, który dociera
    // do trasy administracyjnej KTÓRĄKOLWIEK ścieżką (rola/coordinator/inspector),
    // jest ograniczony do kategorii wskazanych w jego menu_permissions.
    if (hasMenuPermissions && permissionKey && hasAnyAccess) {
      if (!menu.includes(permissionKey)) {
        console.warn(`⛔ [PrivateRoute] Kategoria '${permissionKey}' spoza roli firmowej - przekierowanie do /`);
        return <Navigate to="/" replace />;
      }
    } else if (!hasAnyAccess && !hasCompanyRoleAccess) {
      console.warn("⛔ Brak uprawnień - przekierowanie do /");
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

export default PrivateRoute;
